import { useContext, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext } from "@/context/AuthContext";

// localStorage key — stores ONLY a boolean dismissal flag, not auth/PII/secrets.
// Sensitive data lives in JWT cookies; UI dismissals belong in localStorage.
const GATE_KEY = "primordeia_garden_gate_acknowledged";

const GardenGate = () => {
  const { user, loading } = useContext(AuthContext);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(GATE_KEY) === "true"; }
    catch { return false; }
  });
  const [visible, setVisible] = useState(false);

  // Show only to unauthenticated users who haven't acknowledged yet.
  // Once dismissed, the gate is sticky for this session — no re-render or auth-state
  // flicker can bring it back. (Previously: a brief user → null transition during
  // register could re-trigger this effect; now `dismissed` is the early gate.)
  useEffect(() => {
    if (dismissed) { setVisible(false); return; }
    if (loading) return;
    if (user) { setVisible(false); return; }
    setVisible(true);
  }, [user, loading, dismissed]);

  const handleEnter = () => {
    try { localStorage.setItem(GATE_KEY, "true"); } catch { /* ignore */ }
    setDismissed(true);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="fixed inset-0 z-[400] flex items-center justify-center px-4 py-10 overflow-y-auto"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(26, 40, 22, 0.88) 0%, rgba(14, 22, 12, 0.96) 100%)",
            backdropFilter: "blur(6px)"
          }}
          data-testid="garden-gate-overlay"
        >
          {/* Hanging ropes */}
          <svg
            width="100%" height="72"
            className="absolute top-0 left-0 right-0 pointer-events-none"
            viewBox="0 0 1200 72" preserveAspectRatio="none" aria-hidden="true"
          >
            <defs>
              <pattern id="rope" width="6" height="6" patternUnits="userSpaceOnUse">
                <path d="M0 3 Q1.5 0 3 3 T6 3" stroke="#7a5a30" strokeWidth="1.2" fill="none" opacity="0.7"/>
              </pattern>
            </defs>
            <rect x="380" y="0" width="8" height="70" fill="url(#rope)" />
            <rect x="812" y="0" width="8" height="70" fill="url(#rope)" />
          </svg>

          <motion.div
            initial={{ y: -140, rotate: -3, opacity: 0 }}
            animate={{ y: 0, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 14, stiffness: 110, delay: 0.15 }}
            className="relative w-full max-w-xl mx-auto"
            data-testid="garden-gate-sign"
          >
            {/* Rope attachment points */}
            <div className="absolute -top-6 left-[18%] w-4 h-6 rounded-full"
              style={{ background: "linear-gradient(180deg, #8a6a3a 0%, #5a4020 100%)", boxShadow: "inset 0 -2px 3px rgba(0,0,0,0.4)" }} />
            <div className="absolute -top-6 right-[18%] w-4 h-6 rounded-full"
              style={{ background: "linear-gradient(180deg, #8a6a3a 0%, #5a4020 100%)", boxShadow: "inset 0 -2px 3px rgba(0,0,0,0.4)" }} />

            {/* The wooden sign */}
            <div
              className="relative rounded-[36px] p-10 sm:p-12"
              style={{
                background: `
                  repeating-linear-gradient(90deg,
                    #a07a48 0px, #b48856 12px, #9e7643 20px, #ad8150 32px, #8c6736 44px,
                    #a67d4a 58px, #b48856 70px, #9e7643 82px, #ad8150 96px, #8c6736 110px),
                  linear-gradient(180deg, #b4895a 0%, #8c6636 100%)
                `,
                boxShadow:
                  "0 0 0 5px #5a4020, 0 0 0 8px #3e2b14, 0 24px 64px rgba(0,0,0,0.55), inset 0 2px 0 rgba(255,255,255,0.15), inset 0 -20px 40px rgba(0,0,0,0.25)"
              }}
            >
              {/* Wood grain knots */}
              <div className="absolute top-8 left-10 w-5 h-3 rounded-full opacity-40"
                style={{ background: "radial-gradient(ellipse, #3e2b14 0%, transparent 70%)" }} />
              <div className="absolute bottom-14 right-12 w-7 h-4 rounded-full opacity-35"
                style={{ background: "radial-gradient(ellipse, #3e2b14 0%, transparent 70%)" }} />

              {/* Corner nail heads */}
              {[
                "top-3 left-3", "top-3 right-3", "bottom-3 left-3", "bottom-3 right-3"
              ].map((pos) => (
                <div
                  key={pos}
                  className={`absolute ${pos} w-3 h-3 rounded-full`}
                  style={{
                    background: "radial-gradient(circle at 35% 35%, #d4c29a 0%, #6a5432 60%, #3a2a12 100%)",
                    boxShadow: "inset 0 -1px 1px rgba(0,0,0,0.6)"
                  }}
                  aria-hidden="true"
                />
              ))}

              {/* Little vine garland */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-3xl opacity-80 select-none" aria-hidden="true">
                &#x1F33F;&#x1F33A;&#x1F33F;
              </div>

              {/* Heading */}
              <div
                className="text-center mb-4 text-xs font-bold uppercase tracking-[0.32em]"
                style={{
                  color: "#f7e7c1",
                  fontFamily: "'Cinzel', serif",
                  textShadow: "0 1px 0 #3a2a12, 0 2px 2px rgba(0,0,0,0.4)"
                }}
                data-testid="garden-gate-heading"
              >
                A Word Before You Enter
              </div>

              {/* Divider */}
              <div
                className="h-[2px] mx-auto mb-6"
                style={{
                  width: "40%",
                  background: "linear-gradient(90deg, transparent, #f7e7c1, transparent)",
                  opacity: 0.55
                }}
              />

              {/* The message */}
              <p
                className="text-base sm:text-lg leading-relaxed text-center"
                style={{
                  color: "#2a1a08",
                  fontFamily: "'Cinzel', serif",
                  fontWeight: 600,
                  textShadow: "0 1px 0 rgba(255, 230, 180, 0.4)"
                }}
                data-testid="garden-gate-message"
              >
                &ldquo;At times you may feel some discomfort exploring. There is fear when facing the shadow. It&rsquo;s not a wide open door of superficial frivolity; but, at times, a hard path for the seed to return to the Garden.&rdquo;
              </p>

              {/* Enter button */}
              <div className="flex justify-center mt-8">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleEnter}
                  className="px-8 py-3 rounded-full text-sm font-bold tracking-widest uppercase"
                  style={{
                    background: "linear-gradient(135deg, #3e2b14 0%, #6a4a26 100%)",
                    color: "#f7e7c1",
                    fontFamily: "'Cinzel', serif",
                    letterSpacing: "0.24em",
                    boxShadow:
                      "0 6px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,230,180,0.2), inset 0 -2px 4px rgba(0,0,0,0.4)",
                    border: "1px solid #2a1a08"
                  }}
                  data-testid="garden-gate-enter"
                >
                  I Understand &mdash; Enter the Garden
                </motion.button>
              </div>
            </div>

            {/* Tiny falling leaves for atmosphere */}
            <div className="pointer-events-none absolute -bottom-10 left-8 text-2xl opacity-60 select-none" aria-hidden="true">
              &#x1F343;
            </div>
            <div className="pointer-events-none absolute -bottom-6 right-10 text-2xl opacity-50 select-none" aria-hidden="true">
              &#x1F33F;
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GardenGate;
